import { useState, type MouseEvent, type ReactNode } from 'react';
import {
  getRepositoryCaptureState,
  type CaptureSummaryRepo,
  type CaptureUiState,
} from './captureStateResolver';
import ComparisonDiffModal, {
  type ComparisonPayload,
} from './ComparisonDiffModal';
import {
  getRepositoryComparison,
  triggerAfterCapture,
} from '@/components/smartai/api/captureApi';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface CaptureStatusPanelProps {
  repo: CaptureSummaryRepo & { dbRepoId?: string };
}

/** Corner-strip colors driven only by CaptureUiState. */
export function getCaptureStripColor(state: CaptureUiState): string | null {
  switch (state) {
    case 'READY_TO_COMPARE':
      return '#3b82f6'; // blue — PR accepted, ready for after
    case 'ALREADY_INSTRUMENTED':
      return '#8b5cf6'; // violet — instrumented before onboarding, not by us
    case 'INSTRUMENTATION_FAILED':
      return '#000000'; // black — instrumentation job failed, no PR raised
    case 'WAITING_FOR_PR':
      return '#a3a3a3'; // ash — awaiting PR approval
    case 'PASSED':
      return '#22c55e'; // green — after / compare success
    case 'AFTER_CAPTURE_FAILED':
    case 'BEFORE_CAPTURE_FAILED':
    case 'COMPARE_FAILED':
    case 'CAPTURE_FAILED':
    case 'HAS_DIFFERENCES':
    case 'FAILED':
      return '#ef4444'; // red — failure
    case 'CAPTURE_PENDING':
    case 'COMPARING':
      return '#c4b8a8'; // muted beige while in progress
    default:
      return null;
  }
}

/** Hover line shown under card stats (not in the tech-stack footer). */
export function getCaptureStatusFooterLabel(
  repo: CaptureSummaryRepo,
): string | null {
  const state = getRepositoryCaptureState(repo);
  if (state === 'DISABLED') return null;

  const optedIn = repo.captureOptedInBy || 'User';
  const prLabel = repo.prStatus || repo.instrumentationPr?.status || 'Open';
  const prId = repo.instrumentationPr?.prId;

  switch (state) {
    case 'WAITING_FOR_PR':
      return `Opted in by ${optedIn} · PR ${prId ?? ''} ${String(prLabel).toLowerCase()}`.trim();
    case 'READY_TO_COMPARE':
      return `Opted in by ${optedIn} · PR Approved`;
    case 'ALREADY_INSTRUMENTED':
      return `Opted in by ${optedIn} · Already instrumented before onboarding`;
    case 'INSTRUMENTATION_FAILED':
      return `Opted in by ${optedIn} · Instrumentation failed`;
    case 'PASSED':
    case 'HAS_DIFFERENCES':
    case 'FAILED':
      return `Compared by ${optedIn}`;
    default:
      return `Opted in by ${optedIn}`;
  }
}

/** In-flow footer under metrics — visible on card hover only. */
export function CaptureStatusFooter({ repo }: CaptureStatusPanelProps) {
  const label = getCaptureStatusFooterLabel(repo);
  if (!label) return null;

  return (
    <p
      className={cn(
        'pointer-events-none absolute inset-x-0 bottom-11 z-[2] m-0 truncate px-5 pb-1 text-[0.65rem] leading-none text-[var(--inv-text-muted)]',
        'opacity-0 transition-opacity duration-150',
        'group-hover:opacity-100 group-focus-within:opacity-100',
      )}
      title={label}>
      {label}
    </p>
  );
}

/**
 * Corner strip always visible; labels/buttons top-right on card hover only
 * (parent card must have `group` + `relative` + `overflow-hidden`).
 */
const CaptureStatusPanel = ({ repo }: CaptureStatusPanelProps) => {
  const state = getRepositoryCaptureState(repo);
  const [busy, setBusy] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [report, setReport] = useState<ComparisonPayload | null>(null);

  if (state === 'DISABLED') return null;

  const stripColor = getCaptureStripColor(state);
  const stop = (e: MouseEvent) => e.stopPropagation();

  const openReport = async (e: MouseEvent) => {
    stop(e);
    if (!repo.dbRepoId) return;
    setBusy(true);
    try {
      const data = (await getRepositoryComparison(
        repo.dbRepoId,
      )) as ComparisonPayload;
      setReport(data);
      setReportOpen(true);
    } finally {
      setBusy(false);
    }
  };

  const runCompareNow = async (e: MouseEvent) => {
    stop(e);
    if (!repo.dbRepoId) return;
    setBusy(true);
    try {
      // After capture only — compare is chained by the backend after worker.
      await triggerAfterCapture(repo.dbRepoId);
    } finally {
      setBusy(false);
    }
  };

  let action: ReactNode = null;

  switch (state) {
    case 'WAITING_FOR_PR':
      action = (
        <span className="whitespace-nowrap rounded-md bg-[#e8e8e8] px-2.5 py-1 text-[0.7rem] font-semibold text-[#555] shadow-sm">
          Awaiting PR approval
        </span>
      );
      break;
    case 'READY_TO_COMPARE':
      action = (
        <Button
          type="button"
          size="sm"
          className="h-7 bg-[#3b82f6] text-xs text-white hover:bg-[#2563eb]"
          disabled={busy}
          onClick={runCompareNow}>
          Compare now
        </Button>
      );
      break;
    case 'ALREADY_INSTRUMENTED':
      action = (
        <span className="whitespace-nowrap rounded-md bg-[#ede9fe] px-2.5 py-1 text-[0.7rem] font-semibold text-[#6d28d9] shadow-sm">
          Already instrumented
        </span>
      );
      break;
    case 'INSTRUMENTATION_FAILED':
      action = (
        <span className="whitespace-nowrap rounded-md bg-black px-2.5 py-1 text-[0.7rem] font-semibold text-white shadow-sm">
          Instrumentation Failed
        </span>
      );
      break;
    case 'PASSED':
      action = (
        <Button
          type="button"
          size="sm"
          className="h-7 bg-[#22c55e] text-xs text-white hover:bg-[#16a34a]"
          disabled={busy}
          onClick={openReport}>
          View Comparison
        </Button>
      );
      break;
    case 'HAS_DIFFERENCES':
    case 'FAILED':
      action = (
        <Button
          type="button"
          size="sm"
          className="h-7 bg-[#ef4444] text-xs text-white hover:bg-[#dc2626]"
          disabled={busy}
          onClick={openReport}>
          Review Differences
        </Button>
      );
      break;
    case 'BEFORE_CAPTURE_FAILED':
    case 'AFTER_CAPTURE_FAILED':
    case 'COMPARE_FAILED':
    case 'CAPTURE_FAILED':
      action = (
        <span className="whitespace-nowrap rounded-md bg-red-100 px-2.5 py-1 text-[0.7rem] font-semibold text-red-700 shadow-sm">
          Capture Failed
        </span>
      );
      break;
    case 'COMPARING':
    case 'CAPTURE_PENDING':
      action = (
        <span className="whitespace-nowrap rounded-md bg-[#eee] px-2.5 py-1 text-[0.7rem] font-semibold text-[#555] shadow-sm">
          {state === 'COMPARING' ? 'Comparing…' : 'Capturing…'}
        </span>
      );
      break;
    default:
      break;
  }

  return (
    <>
      {/* Top-right corner strip — visible by default, hidden on hover */}
      {stripColor ? (
        <div
          aria-hidden="true"
          title={state}
          className={cn(
            'pointer-events-none absolute right-0 top-0 z-[15] h-7 w-7',
            'opacity-100 transition-opacity duration-150',
            'group-hover:opacity-0 group-focus-within:opacity-0',
          )}
          style={{
            backgroundColor: stripColor,
            clipPath: 'polygon(0 0, 100% 0, 100% 100%)',
          }}
        />
      ) : null}

      {/* Labels / buttons — top-right, hover only (strip hides) */}
      <div
        className={cn(
          'pointer-events-none absolute right-2.5 top-2.5 z-[16] flex max-w-[70%] flex-col items-end gap-1',
          'opacity-0 transition-opacity duration-150',
          'group-hover:pointer-events-auto group-hover:opacity-100',
          'group-focus-within:pointer-events-auto group-focus-within:opacity-100',
        )}
        onClick={stop}>
        {action}
      </div>

      <ComparisonDiffModal
        open={reportOpen}
        onOpenChange={setReportOpen}
        data={report}
        title={
          state === 'PASSED' ? 'View Comparison' : 'Review Differences'
        }
      />
    </>
  );
};

export default CaptureStatusPanel;
